import { getBase64 } from '@repo/lib/fs/getBase64';
import { DownloadIcon, Maximize2Icon, Minimize2Icon } from 'lucide-react';
import { ComponentProps, useMemo } from 'react';

import type { MediaViewerFile } from '@/components/features/media/MediaViewer';

import { Button } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FullscreenRoot, FullscreenTarget, FullscreenTrigger } from '@/components/ui/fullscreen';
import { Image } from '@/components/ui/image';
import { getFileUrl } from '@/utils/file';

export default function MediaImageViewer(mediaViewerFile: MediaViewerFile) {
  const { file, open, onOpenChange } = mediaViewerFile;
  const isFilesInstance = 'id' in file;

  const [fileURL, type] = useMemo<[string, string]>(() => {
    if ('id' in file) {
      return [getFileUrl(file.id), file.fileType];
    } else if (file instanceof File) {
      return [URL.createObjectURL(file), file.type];
    }
    return ['', ''];
  }, [file]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FullscreenRoot>
        <FullscreenTarget asChild fullscreenClassName="fixed inset-0 w-screen h-screen max-w-none max-h-none rounded-none shadow-none top-0 left-0 translate-x-0 translate-y-0">
          <DialogContent className={'h-full max-h-224'}>
            <DialogBody>
              <DialogHeader>
                <DialogTitle>{file.name}</DialogTitle>
                <DialogDescription className={'sr-only'}>{file.name} - podgląd</DialogDescription>
              </DialogHeader>
              <MediaImageViewerContent src={fileURL} type={type} alt={file.name} blurDataURL={isFilesInstance ? (file.thumbnail ?? undefined) : undefined} />
            </DialogBody>
            <DialogFooter className="space-x-0.5">
              <FullscreenTrigger asChild>
                {(isFullscreen) => (
                  <Button variant="ghost" size="sm" aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                    {isFullscreen ? <Minimize2Icon /> : <Maximize2Icon />}
                  </Button>
                )}
              </FullscreenTrigger>
              {isFilesInstance ? (
                <a href={fileURL} download target="_self">
                  <Button variant={'ghost'} size="sm">
                    <DownloadIcon />
                  </Button>
                </a>
              ) : null}
            </DialogFooter>
          </DialogContent>
        </FullscreenTarget>
      </FullscreenRoot>
    </Dialog>
  );
}

export function MediaImageViewerContent(
  mediaImageViewerContentProps: ComponentProps<typeof Image> & {
    type: string;
  }
) {
  const { blurDataURL, src, type, ...props } = mediaImageViewerContentProps;

  const base64 = getBase64({
    base64: blurDataURL,
    fileType: type,
    clear: true,
  });

  return (
    <div className={'relative grow'}>
      <Image src={src} placeholder={'blur'} blurDataURL={base64} fill {...props} />
    </div>
  );
}
